import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from "react";
import {
  createCustomer,
  getAllBranch,
  getAllRoutes,
  getAllRoutesByCompany,
  getChannels,
  getCustomerDetails,
  getCustomerGroups,
  getPricebooks,
  readPricebookBindingRefusal,
  updateCustomer,
} from "../../services";
import { getBranchId } from "../../services/keycloak";
import {
  CustomerBilltype,
  CustomerChannel,
  CustomerCreateRequest,
  CustomerGroup,
  CustomerResponse,
  CustomerTaxtype,
  CustomerUpdateRequest,
  CustomerTaxIdType,
} from "../../types/customer.type";
import { CustomerServiceMode } from "../../types/special-order.type";
import { SERVICE_MODE_OPTIONS } from "../SpecialOrder/data";
import { PriceBookResponse } from "../../types/price-book.type";
import { Branch, Route } from "../../types/user.type";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomerLocationPicker from "./CustomerLocationPicker";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";
import { useUserRole } from "../../utils/hooks/useUserRole";
import {
  findTextSetting,
  useSystemSettings,
} from "../../utils/hooks/useSystemSettings";
import { SYSTEM_SETTING_KEY } from "../../types/system-setting.type";

const compact = "@media " + COMPACT_QUERY;

/** One request covers the whole catalogue; it holds a few dozen pricebooks. */
const PRICE_BOOK_LIMIT = 100;

interface CustomerDrawerProps {
  customerId?: string;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

interface CustomerForm {
  shopName: string;
  contactName: string;
  phone: string;
  billType: CustomerBilltype;
  customerGroupUuid: string;
  channelUuid: string;
  storeNumber: string;
  routeUuid: string;
  branchUuid: string;
  addressNo: string;
  addressLine2: string;
  addressStreet: string;
  addressProvince: string;
  addressSubdistrict: string;
  addressDistrict: string;
  addressZipcode: string;
  vatType: CustomerTaxtype;
  priceBook: string;
  creditTerm: string;
  wantTaxInvoice: boolean;
  // "" = nobody has said. The type classifies an identifier, so with no identifier
  // there is nothing to classify — and defaulting the DISPLAY to นิติบุคคล asserted a
  // classification the record does not hold, which read to QA as the value reverting.
  invTaxIdType: CustomerTaxIdType | "";
  invTaxId: string;
  invName: string;
  invBranchCode: string;
  invBranchName: string;
  invEmail: string;
  invAddress: string;
  invZipcode: string;
  sameAddressAsCustomer: boolean;
  latitude: number | null;
  longitude: number | null;
  // ออเดอร์พิเศษ default route choice; "" = ไม่ระบุ.
  serviceMode: CustomerServiceMode | "";
}

type CustomerFormErrors = Partial<
  Record<
    | "shopName"
    | "contactName"
    | "phone"
    | "customerGroupUuid"
    | "channelUuid"
    | "storeNumber"
    | "routeUuid"
    | "branchUuid"
    | "priceBook"
    | "creditTerm"
    | "addressNo"
    | "addressZipcode"
    | "invTaxId"
    | "invTaxIdType"
    | "invBranchCode"
    | "invName"
    | "invAddress"
    | "invZipcode",
    string
  >
>;

/**
 * Which input a rejected field belongs to.
 *
 * The API names columns; the form names inputs. Both halves of the buyer block map to the
 * control that can actually fix them: the API falls back across blocks when it judges a
 * shop (an empty `inv_name` means the buyer name IS `name`), so a complaint about `inv_name`
 * on a shop that has none is answered by the shop-name field.
 */
const API_FIELD_TO_INPUT: Record<string, keyof CustomerFormErrors> = {
  name: "shopName",
  contact_name: "contactName",
  phone: "phone",
  store_number: "storeNumber",
  address1: "addressNo",
  zipcode: "addressZipcode",
  creditterm: "creditTerm",
  creditterm_type: "creditTerm",
  inv_name: "invName",
  inv_taxid: "invTaxId",
  inv_taxid_type: "invTaxId",
  inv_branchcode: "invBranchCode",
  inv_address: "invAddress",
  inv_zipcode: "invZipcode",
};

/** The API's per-field complaints, reduced to the ones this form can show. */
const fieldErrorsFrom = (error: unknown): CustomerFormErrors => {
  const body = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  if (!body || typeof body !== "object") return {};

  const mapped: CustomerFormErrors = {};
  for (const [field, message] of Object.entries(body as Record<string, unknown>)) {
    const input = API_FIELD_TO_INPUT[field];
    if (input && typeof message === "string") mapped[input] = message;
  }
  return mapped;
};

interface DropdownOption {
  value: string;
  label: string;
}

const INITIAL_CUSTOMER: CustomerForm = {
  shopName: "",
  contactName: "",
  phone: "",
  billType: "cash",
  customerGroupUuid: "",
  channelUuid: "",
  storeNumber: "",
  routeUuid: "",
  branchUuid: "",
  addressNo: "",
  addressLine2: "",
  addressStreet: "",
  addressProvince: "",
  addressSubdistrict: "",
  addressDistrict: "",
  addressZipcode: "",
  vatType: "EX",
  priceBook: "",
  creditTerm: "",
  wantTaxInvoice: false,
  invTaxIdType: "",
  invTaxId: "",
  invName: "",
  invBranchCode: "",
  invBranchName: "",
  invEmail: "",
  invAddress: "",
  invZipcode: "",
  sameAddressAsCustomer: true,
  latitude: null,
  longitude: null,
  serviceMode: "",
};

const BILL_TYPES: { value: CustomerBilltype; label: string }[] = [
  { value: "cash", label: "ขายสด" },
  { value: "credit", label: "ขายเชื่อ" },
];

// Fields whose value feeds the derived customer_code. Changing any of them makes the
// backend re-issue the code; leaving them alone leaves the code untouched. Labels match
// the field captions rendered below so the warning dialog can name them.
const CODE_AFFECTING_LABELS = {
  customerGroup: "หมวดลูกค้า",
  channel: "ช่องทางการขาย",
  route: "สาย (Route)",
  creditTerm: "เครดิตเทอม",
  storeNumber: "หมายเลขร้าน",
} as const;

const CODE_AFFECTING_HINT = "การแก้ไขค่านี้จะทำให้รหัสลูกค้าเปลี่ยน";

// Without the settings the form cannot tell the general group from the others.
const SETTINGS_LOAD_ERROR =
  "โหลดการตั้งค่าระบบไม่สำเร็จ จึงยังบันทึกไม่ได้ กรุณาปิดแล้วเปิดใหม่อีกครั้ง";

// For the general group (Z) the store number is the 4-digit tail of the customer_code
// (D1-D6 + tail). Blank lets the backend run the number; clearing a stored one keeps the
// current code rather than re-issuing it.
const Z_STORE_NUMBER_HELPER =
  "เลขท้าย 4 หลักของรหัสลูกค้า (เช่น 0033) — เว้นว่างเพื่อรันเลขอัตโนมัติ";

/** Mirrors the backend rule for a Z store number: "0033" or "00033", value 1-9999. */
const zStoreNumberError = (value: string): string | undefined => {
  if (!/^\d{4,5}$/.test(value))
    return "หมายเลขร้านต้องเป็นตัวเลข 4-5 หลัก (เช่น 0033)";
  if (value.length === 5 && !value.startsWith("0"))
    return "หมายเลขร้าน 5 หลักต้องขึ้นต้นด้วย 0 (สูงสุด 9999)";
  if (Number(value) === 0) return "หมายเลขร้านต้องไม่เป็น 0";
  return undefined;
};

// HO's wording for anyone about to put a customer on eTax: every document filed with KTB is
// billed and cannot be recalled. Shown above the checkbox and again in the save confirmation.
const EtaxCostWarning = () => (
  <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
      ⚠️ คำเตือน: การเลือกส่ง E-Tax อาจมีผลต่อค่าใช้จ่าย
      โปรดตรวจสอบข้อมูลให้ถูกต้องก่อนยืนยันรายการ
    </Typography>
    <Typography variant="body2" sx={{ mb: 0.75 }}>
      การทำเครื่องหมาย ✓ ในช่อง E-Tax
      หมายถึงการระบุว่าลูกค้ารายนี้ต้องการรับใบกำกับภาษีอิเล็กทรอนิกส์ (E-Tax)
    </Typography>
    <Typography variant="body2">
      หมายเหตุ: หากไม่มั่นใจว่าลูกค้าต้องการรับ E-Tax หรือไม่
      กรุณาติดต่อสำนักงานใหญ่เพื่อตรวจสอบข้อมูลก่อนทำเครื่องหมายยืนยัน
    </Typography>
  </Alert>
);

// Marker shown under every field that can move the customer_code.
const CodeAffectingHint = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 0.5,
      mt: -1.25,
      mb: 2,
      [compact]: { mb: 1.25 },
      color: "text.secondary",
    }}
  >
    <InfoOutlinedIcon sx={{ fontSize: 14, mt: "2px", flexShrink: 0 }} />
    <Typography variant="caption">{CODE_AFFECTING_HINT}</Typography>
  </Box>
);

// Tax-invoice block normalization (see Customer/Tax block spec).
const cleanTax = (value?: string | null) => {
  const text = (value ?? "").toString().trim();
  return text === "." || text === "-" ? "" : text;
};

const joinTaxParts = (parts: (string | null | undefined)[]) =>
  parts
    .map((part) => (part ?? "").toString().trim())
    .filter(Boolean)
    .join(" ");

const CREDIT_TERM_OPTIONS: DropdownOption[] = [
  { value: "1", label: "1 วัน" },
  { value: "3", label: "3 วัน" },
  { value: "7", label: "7 วัน" },
  { value: "15", label: "15 วัน" },
  { value: "30", label: "30 วัน" },
  { value: "45", label: "45 วัน" },
  { value: "60", label: "60 วัน" },
  { value: "NEXT_BILL_CYCLE", label: "รอบบิลถัดไป" },
  { value: "UNSPECIFIED", label: "ไม่ระบุ" },
];

const DAY_CREDIT_TERMS = new Set(["1", "3", "7", "15", "30", "45", "60"]);

const toText = (value?: string | null) => value ?? "";
const toDigitsOnly = (value: string) => value.replace(/\D/g, "");

const joinAddress = (...parts: string[]) => parts.filter(Boolean).join(" ");

const FieldLabel = ({
  children,
  required = false,
}: {
  children: ReactNode;
  required?: boolean;
}) => (
  <Typography variant="body2" fontWeight={500} sx={{ mb: 0.75 }}>
    {children}
    {required ? (
      <Box component="span" sx={{ color: "#ff9f9f", ml: 0.5 }}>
        *
      </Box>
    ) : null}
  </Typography>
);

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <Typography
    sx={{
      mb: 2,
      [compact]: { mb: 1.25 },
      fontSize: 14,
      fontWeight: 700,
      textDecoration: "underline",
    }}
  >
    {children}
  </Typography>
);

const creditTermFromResponse = (customer: CustomerResponse): string => {
  const type = customer.creditterm_type;
  if (type === "NEXT_BILL_CYCLE" || type === "UNSPECIFIED") return type;
  if (type === "DAYS" || customer.creditterm > 0) {
    const days = String(customer.creditterm);
    if (DAY_CREDIT_TERMS.has(days)) return days;
  }
  return "UNSPECIFIED";
};

const mapCustomerToForm = (customer: CustomerResponse): CustomerForm => {
  const invoiceAddress = toText(customer.inv_address);
  const customerAddress = joinAddress(
    toText(customer.address1),
    toText(customer.address2),
    toText(customer.street),
    toText(customer.subdistrict),
    toText(customer.district),
    toText(customer.province),
  );
  const sameAddress =
    customer.issuing_address === "customer_address" ||
    (!invoiceAddress && customer.issuing_address !== "inv_address") ||
    invoiceAddress === customerAddress;

  return {
    shopName: toText(customer.name),
    contactName: toText(customer.contact_name),
    phone: toText(customer.phone),
    billType: (customer.billtype as CustomerBilltype) || "cash",
    customerGroupUuid: toText(customer.customer_group_uuid),
    channelUuid: toText(customer.channel_uuid),
    storeNumber: toText(customer.store_number),
    routeUuid: toText(customer.route_uuid),
    branchUuid: "",
    addressNo: toText(customer.address1),
    addressLine2: toText(customer.address2),
    addressStreet: toText(customer.street),
    addressProvince: toText(customer.province),
    addressSubdistrict: toText(customer.subdistrict),
    addressDistrict: toText(customer.district),
    addressZipcode: toText(customer.zipcode),
    vatType: customer.taxtype === "IN" ? "IN" : "EX",
    priceBook: toText(customer.pricebook),
    creditTerm: creditTermFromResponse(customer),
    // Read the stored intent instead of inferring it. The old guess -- "has an invoice name
    // or a tax id" -- could not tell a shop that wants eTax but has not been fully typed in
    // from one that never wanted it, which is why the pending-eTax figure never meant one
    // thing. Legacy rows carry false and are shown as paper until accounting says otherwise.
    wantTaxInvoice: Boolean(customer.etax_enabled),
    // A stored type wins. Failing that, an identifier with no type is read as corporate,
    // which is what every shop predating the column is: the inference this replaced said
    // the same thing, and re-asking 9,000 shops to restate it would block edits nobody
    // came to make. Only a shop with NO identifier shows blank, because that is the one
    // case where no classification was ever implied.
    invTaxIdType:
      (customer.inv_taxid_type as CustomerTaxIdType) ||
      (toText(customer.inv_taxid).trim() ? "TXID" : ""),
    invTaxId: toText(customer.inv_taxid),
    invName: toText(customer.inv_name),
    invBranchCode: toText(customer.inv_branchcode),
    invBranchName: toText(customer.inv_branchname),
    invEmail: toText(customer.email),
    invAddress: invoiceAddress,
    invZipcode: toText(customer.inv_zipcode),
    sameAddressAsCustomer: sameAddress,
    latitude: customer.latitude ?? null,
    longitude: customer.longitude ?? null,
    serviceMode: customer.service_mode ?? "",
  };
};

// The creditterm pair the form currently represents, or null when the customer is on
// cash terms (nothing to send). Shared by the payload builder and the change detection so
// the two can never disagree about what "the credit term changed" means.
const resolveCreditTerm = (
  form: CustomerForm,
): Pick<CustomerCreateRequest, "creditterm_type" | "creditterm"> | null => {
  if (form.billType === "cash" || !form.creditTerm) return null;
  if (form.creditTerm === "NEXT_BILL_CYCLE")
    return { creditterm_type: "NEXT_BILL_CYCLE", creditterm: null };
  if (form.creditTerm === "UNSPECIFIED")
    return { creditterm_type: "UNSPECIFIED", creditterm: null };
  return { creditterm_type: "DAYS", creditterm: Number(form.creditTerm) };
};

// One credit-term policy, spelled one way. The stored row and the form disagree on how to
// say "no day count": legacy customers carry `creditterm = 0` where the current contract
// carries null, and the number means nothing at all unless the type is DAYS. Comparing the
// raw fields reported a change on ~99% of the customer base — every save then warned the
// user about a code change they had not asked for, and sent the pair on to the backend,
// which re-derived the code from it.
const normalizeCreditTerm = (
  type: string | null | undefined,
  days: number | null | undefined,
): { type: string; days: number | null } => {
  const resolvedType = toText(type) || "UNSPECIFIED";
  if (resolvedType !== "DAYS") return { type: resolvedType, days: null };
  return { type: resolvedType, days: days != null && days > 0 ? days : null };
};

/** KTB buyer types (spec v1.6 §3.2), in the order a shop is most likely to be one. */
const BUYER_TYPE_OPTIONS: { value: CustomerTaxIdType; label: string }[] = [
  { value: "TXID", label: "นิติบุคคล" },
  { value: "NIDN", label: "บุคคลธรรมดา" },
  { value: "CCPT", label: "หนังสือเดินทาง" },
  { value: "OTHR", label: "อื่น ๆ" },
];

/** What the identifier is called for each type — it is not "เลขที่ผู้เสียภาษี" for all four. */
const BUYER_ID_LABEL: Record<CustomerTaxIdType, string> = {
  TXID: "เลขประจำตัวผู้เสียภาษี",
  NIDN: "เลขบัตรประชาชน",
  CCPT: "เลขหนังสือเดินทาง",
  OTHR: "ตัวระบุผู้ซื้อ",
};

const buildPayload = (
  form: CustomerForm,
  isHo: boolean,
  isGeneralGroup: boolean,
  canSetEtaxIntent: boolean,
): CustomerCreateRequest => {
  const payload: CustomerCreateRequest = {
    name: form.shopName.trim(),
    customer_group_uuid: form.customerGroupUuid,
    route_uuid: form.routeUuid,
    contact_name: form.contactName.trim(),
    phone: form.phone,
    billtype: form.billType,
    taxtype: form.vatType,
    pricebook: form.priceBook,
    issuing_address: form.sameAddressAsCustomer
      ? "customer_address"
      : "inv_address",
    address1: form.addressNo || null,
    address2: form.addressLine2 || null,
    street: form.addressStreet || null,
    subdistrict: form.addressSubdistrict || null,
    district: form.addressDistrict || null,
    province: form.addressProvince || null,
    zipcode: form.addressZipcode || null,
    country: "TH",
    latitude: form.latitude,
    longitude: form.longitude,
  };

  if (isHo) payload.branch_uuid = form.branchUuid;

  // Only when chosen: a shop nobody classified stays null, which is also what an API that
  // predates the column would expect.
  if (form.serviceMode) payload.service_mode = form.serviceMode;

  // The sales channel belongs to the general group (Z) only; omit otherwise.
  if (isGeneralGroup && form.channelUuid) payload.channel_uuid = form.channelUuid;

  // Store number for every group: MT pads it into D6-D10, Z uses it as the 4-digit tail.
  // Blank is omitted here; buildUpdatePayload states a cleared Z value as null.
  if (form.storeNumber.trim()) payload.store_number = form.storeNumber.trim();

  // Credit term only applies to non-cash customers; omit the pair when
  // nothing is selected.
  const creditTerm = resolveCreditTerm(form);
  if (creditTerm) {
    payload.creditterm_type = creditTerm.creditterm_type;
    payload.creditterm = creditTerm.creditterm;
  }

  // Sent only by a caller allowed to move it. The backend refuses the field outright from
  // anyone else and lets a request that never mentions it through untouched, so omitting it
  // is what keeps everybody else's edits working.
  if (canSetEtaxIntent) payload.etax_enabled = form.wantTaxInvoice;

  // KTB delivers the signed tax invoice to this address, but it is contact detail, not part
  // of the header — it is sent for every customer so that clearing it reaches the API.
  // Cleared means cleared: null rather than "", which the API would store as a blank the
  // submit gate then has to reason about.
  payload.email = form.invEmail.trim() || null;

  // The header block goes with any customer who has one, on eTax or not: the same block
  // prints on a paper bill, and a customer accounting switches to eTax later then already
  // has its details on file.
  //
  // Every field is sent on every save, empty ones as null. Sending them only when filled
  // read a cleared field as an unmentioned one — the API leaves an unmentioned field alone,
  // so a value deleted on screen came back on the next load and the form quietly disagreed
  // with the record. Back office owns this whole block on this screen; nothing here is
  // locked, so there is no field to protect by staying silent about it.
  //
  // The buyer type is the one exception, and it is not a value the user typed: it is a
  // classification OF an identifier. With no identifier and no eTax there is nothing to
  // classify, so it goes as null rather than stamping the "นิติบุคคล" default on a paper
  // shop nobody asked about.
  payload.inv_taxid_type =
    form.invTaxIdType && (form.wantTaxInvoice || form.invTaxId.trim())
      ? form.invTaxIdType
      : null;
  payload.inv_name = form.invName.trim() || null;
  payload.inv_taxid = form.invTaxId.trim() || null;
  // Only a corporate buyer carries a branch — KTB rejects a trader id built with one for
  // anyone else — and the field is hidden for the other three types. Hidden has to mean
  // CLEARED, not "leave whatever is there": a shop moved off นิติบุคคล keeps a branch code
  // the form can no longer show, the API refuses a branch code on a personal or passport
  // buyer, and the operator has no control left to fix it with.
  payload.inv_branchcode =
    form.invTaxIdType === "TXID" ? form.invBranchCode.trim() || null : null;
  payload.inv_branchname = form.invBranchName.trim() || null;

  if (!form.sameAddressAsCustomer) {
    payload.inv_address = form.invAddress.trim() || null;
    payload.inv_zipcode = form.invZipcode || null;
  } else {
    // Shared address: this screen has no issuing_address control, so "same address" is
    // expressed by copying the shop's own address into the header block.
    payload.inv_address =
      joinAddress(
        form.addressNo,
        form.addressLine2,
        form.addressStreet,
        form.addressSubdistrict,
        form.addressDistrict,
        form.addressProvince,
      ) || null;
    payload.inv_zipcode = form.addressZipcode || null;
  }

  return payload;
};

export default function CustomerDrawer({
  customerId,
  open,
  onClose,
  onSaved,
}: CustomerDrawerProps) {
  // Head office is a ROLE, not the absence of a branch: HO tokens still carry a
  // branch_code (the head-office branch), so keying off getBranchCode() hid the whole
  // branch block from the users who need it. Detect it from the group, the same way
  // Customer/Filter.tsx and the rest of the app already do.
  const {
    isHeadOfficeAccounting,
    isMaster,
    isAdmin,
    isBranchAccounting,
    isBranchFinance,
    isBranchFinanceAndAccount,
  } = useUserRole();
  const isHo = Boolean(isHeadOfficeAccounting || isMaster);
  /**
   * Who may decide a shop gets an electronic tax invoice.
   *
   * Accounting, branch finance and head office — the people who answer for what was filed
   * with the Revenue Department. Mirrors ETAX_INTENT_ROLES on the backend, which is the
   * authority: this only decides whether the control is shown, and hiding a control is not
   * a permission check.
   */
  const canSetEtaxIntent = Boolean(
    isAdmin ||
      isMaster ||
      isHeadOfficeAccounting ||
      isBranchAccounting ||
      isBranchFinance ||
      isBranchFinanceAndAccount,
  );

  const [form, setForm] = useState<CustomerForm>({ ...INITIAL_CUSTOMER });
  const [errors, setErrors] = useState<CustomerFormErrors>({});
  const [pricebooks, setPricebooks] = useState<PriceBookResponse[]>([]);
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const [channels, setChannels] = useState<CustomerChannel[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [loadedCustomer, setLoadedCustomer] = useState<CustomerResponse | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState<boolean>(false);

  // The general-customer group (ลูกค้าทั่วไป) is the ONLY group that carries a sales channel;
  // every other group is a named Modern Trade chain and carries a store number instead. Which
  // group it is comes from ตั้งค่าระบบ, so the form waits for the settings before it shows.
  const {
    settings: systemSettings,
    loading: loadingSettings,
    hasError: hasSettingsError,
    reload: reloadSettings,
  } = useSystemSettings();
  const generalGroupCode = findTextSetting(
    systemSettings,
    SYSTEM_SETTING_KEY.customerGeneralGroupCode,
  );
  const isGeneralGroupUuid = (groupUuid: string) =>
    generalGroupCode !== undefined &&
    customerGroups.find((group) => group.uuid === groupUuid)?.code ===
      generalGroupCode;
  const isPreparingForm = loadingDetails || loadingSettings;

  const selectedGroupPrefix = useMemo(
    () =>
      customerGroups.find((group) => group.uuid === form.customerGroupUuid)
        ?.prefix,
    [customerGroups, form.customerGroupUuid],
  );
  const isGeneralGroup = isGeneralGroupUuid(form.customerGroupUuid);
  // Store number applies to every group: MT (D6-D10) and Z (4-digit code tail).
  const showStoreNumber = Boolean(selectedGroupPrefix);
  // Whether the store number differs from what the server holds (always true on create).
  const storeNumberChanged =
    !loadedCustomer ||
    form.storeNumber.trim() !== toText(loadedCustomer.store_number);
  const isCash = form.billType === "cash";

  // Whether this save switches the customer onto or off eTax (a new customer ticked counts as
  // "on"). Only someone who can send the flag can change it, so nobody else is ever asked.
  const etaxChange: "on" | "off" | null = (() => {
    if (!canSetEtaxIntent) return null;
    if (form.wantTaxInvoice === Boolean(loadedCustomer?.etax_enabled)) return null;
    return form.wantTaxInvoice ? "on" : "off";
  })();

  // Which customer_code inputs the user actually moved, compared against what the server
  // currently holds. Comparing to `loadedCustomer` (rather than to a snapshot of the form)
  // keeps this correct while the uuid-resolution effects below are still settling.
  const changedCodeFields = useMemo(() => {
    if (!loadedCustomer) return [] as string[];
    const changed: string[] = [];

    const groupCode = customerGroups.find(
      (group) => group.uuid === form.customerGroupUuid,
    )?.code;
    if (groupCode && groupCode !== toText(loadedCustomer.group))
      changed.push(CODE_AFFECTING_LABELS.customerGroup);

    const channelCode = channels.find(
      (channel) => channel.uuid === form.channelUuid,
    )?.code;
    if (
      isGeneralGroup &&
      channelCode &&
      channelCode !== toText(loadedCustomer.channel)
    )
      changed.push(CODE_AFFECTING_LABELS.channel);

    const routeNumber = routes.find(
      (route) => route.uuid === form.routeUuid,
    )?.route_number;
    if (routeNumber && routeNumber !== toText(loadedCustomer.route_number))
      changed.push(CODE_AFFECTING_LABELS.route);

    const creditTerm = resolveCreditTerm(form);
    const baseline = normalizeCreditTerm(
      loadedCustomer.creditterm_type,
      loadedCustomer.creditterm,
    );
    const next = normalizeCreditTerm(
      creditTerm?.creditterm_type ?? "UNSPECIFIED",
      creditTerm?.creditterm ?? null,
    );
    if (next.type !== baseline.type || next.days !== baseline.days)
      changed.push(CODE_AFFECTING_LABELS.creditTerm);

    // Clearing a Z store number keeps the current code, so it is not a code change —
    // buildUpdatePayload still sends it (as null) via storeNumberChanged.
    if (
      showStoreNumber &&
      storeNumberChanged &&
      !(isGeneralGroup && !form.storeNumber.trim())
    )
      changed.push(CODE_AFFECTING_LABELS.storeNumber);

    return changed;
  }, [
    loadedCustomer,
    customerGroups,
    channels,
    routes,
    form,
    isGeneralGroup,
    showStoreNumber,
    storeNumberChanged,
  ]);

  // Read again on every open: MASTER may have pointed the general group elsewhere meanwhile.
  useEffect(() => {
    if (open) reloadSettings();
  }, [open, reloadSettings]);

  useEffect(() => {
    if (!open) return;

    const onGetCustomerGroups = async () => {
      const {
        data: { data },
      } = await getCustomerGroups();
      setCustomerGroups(Array.isArray(data) ? data : (data.rows ?? []));
    };

    const onGetChannels = async () => {
      const {
        data: { data },
      } = await getChannels();
      setChannels(Array.isArray(data) ? data : (data.rows ?? []));
    };

    onGetCustomerGroups();
    onGetChannels();

    if (isHo) {
      const onGetBranches = async () => {
        const {
          data: { data },
        } = await getAllBranch();
        setBranches(Array.isArray(data) ? data : (data.rows ?? []));
      };
      onGetBranches();
    }
  }, [open, isHo]);

  useEffect(() => {
    if (!open) return;

    // HO is not bound to a branch, so /routes/by-company (scoped to the caller's own
    // branch_id) would return the head-office company's routes no matter which branch was
    // picked. Send the SELECTED branch's company_id instead — same pattern as Filter.tsx.
    const onGetRoutes = async (companyId?: string) => {
      const {
        data: { data },
      } = companyId ? await getAllRoutes(companyId) : await getAllRoutesByCompany();
      setRoutes(Array.isArray(data) ? data : (data.rows ?? []));
    };

    if (isHo) {
      if (!form.branchUuid) {
        setRoutes([]);
        return;
      }
      const companyId = branches.find(
        (branch) => branch.uuid === form.branchUuid,
      )?.company_id;
      // branches may not have loaded yet — the effect re-runs once they do.
      if (!companyId) {
        setRoutes([]);
        return;
      }
      onGetRoutes(companyId);
      return;
    }

    onGetRoutes();
  }, [open, isHo, form.branchUuid, branches]);

  // A shop may only be put on the pricebooks open in its branch: an existing shop's own, the
  // branch head office picks for a new one, else the creator's own branch (where a new shop is
  // filed). Null while that branch is not known yet.
  const resolveShopBranchCompanyId = (): string | null => {
    if (customerId) return loadedCustomer?.company_id ?? null;
    if (isHo) {
      return (
        branches.find((branch) => branch.uuid === form.branchUuid)?.company_id ??
        null
      );
    }
    return getBranchId();
  };
  const shopBranchCompanyId = resolveShopBranchCompanyId();

  useEffect(() => {
    if (!open) return;
    if (!shopBranchCompanyId) {
      setPricebooks([]);
      return;
    }

    // A slower answer for a branch picked earlier must not replace the current branch's list.
    let ignored = false;
    const onGetPricebooks = async () => {
      const {
        data: { data },
      } = await getPricebooks(PRICE_BOOK_LIMIT, shopBranchCompanyId);
      if (!ignored) {
        setPricebooks(Array.isArray(data) ? data : (data.rows ?? []));
      }
    };
    onGetPricebooks();

    return () => {
      ignored = true;
    };
  }, [open, shopBranchCompanyId]);

  useEffect(() => {
    const onGetCustomerDetails = async (id: string) => {
      setLoadingDetails(true);
      try {
        const {
          data: { data },
        } = await getCustomerDetails(id);
        setLoadedCustomer(data);
        setForm(mapCustomerToForm(data));
      } finally {
        setLoadingDetails(false);
      }
    };

    if (open && customerId) onGetCustomerDetails(customerId);
  }, [open, customerId]);

  useEffect(() => {
    if (open && !customerId) {
      setForm({ ...INITIAL_CUSTOMER });
      setErrors({});
      setLoadedCustomer(null);
    }
  }, [open, customerId]);

  // The customer detail returns group/channel/route as CODES, not uuids.
  // Resolve them against the option lists once both are available.
  useEffect(() => {
    if (!loadedCustomer?.group || customerGroups.length === 0) return;
    const uuid = customerGroups.find(
      (group) => group.code === loadedCustomer.group,
    )?.uuid;
    if (uuid) setField("customerGroupUuid", uuid);
  }, [loadedCustomer, customerGroups]);

  useEffect(() => {
    if (!loadedCustomer?.channel || channels.length === 0) return;
    const uuid = channels.find(
      (channel) => channel.code === loadedCustomer.channel,
    )?.uuid;
    if (uuid) setField("channelUuid", uuid);
  }, [loadedCustomer, channels]);

  // The customer stores only company_id (the tenant), never a branch uuid. Map it back to
  // the branch so the select shows the right one and the route list loads for that branch.
  useEffect(() => {
    if (!loadedCustomer?.company_id || branches.length === 0) return;
    const uuid = branches.find(
      (branch) => String(branch.company_id) === String(loadedCustomer.company_id),
    )?.uuid;
    if (uuid) setField("branchUuid", uuid);
  }, [loadedCustomer, branches]);

  useEffect(() => {
    if (!loadedCustomer?.route_number || routes.length === 0) return;
    const uuid = routes.find(
      (route) => route.route_number === loadedCustomer.route_number,
    )?.uuid;
    if (uuid) setField("routeUuid", uuid);
  }, [loadedCustomer, routes]);

  const setField = <K extends keyof CustomerForm>(
    field: K,
    value: CustomerForm[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const clearError = (field: keyof CustomerFormErrors) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleTextChange =
    (field: keyof CustomerForm, errorField?: keyof CustomerFormErrors) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setField(field, event.target.value as never);
      if (errorField) clearError(errorField);
    };

  const handleDigitChange =
    (
      field: keyof CustomerForm,
      errorField?: keyof CustomerFormErrors,
      maxLength?: number,
    ) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setField(
        field,
        toDigitsOnly(event.target.value).slice(0, maxLength) as never,
      );
      if (errorField) clearError(errorField);
    };

  const handleClose = () => {
    if (loading) return;
    onClose();
    setForm({ ...INITIAL_CUSTOMER });
    setErrors({});
  };

  const closeAfterSave = () => {
    onClose();
    setForm({ ...INITIAL_CUSTOMER });
    setErrors({});
  };

  const validate = () => {
    const nextErrors: CustomerFormErrors = {};

    if (!form.shopName.trim())
      nextErrors.shopName = "กรุณากรอกชื่อลูกค้า/ชื่อร้านค้า";
    if (!form.contactName.trim())
      nextErrors.contactName = "กรุณากรอกชื่อผู้ติดต่อ";
    if (form.phone && !/^\d{9,10}$/.test(form.phone)) {
      nextErrors.phone = "เบอร์โทรต้องเป็นตัวเลข 9-10 หลัก";
    }
    if (isHo && !form.branchUuid) nextErrors.branchUuid = "กรุณาเลือกสาขา";
    if (!form.customerGroupUuid)
      nextErrors.customerGroupUuid = "กรุณาเลือกหมวดลูกค้า";
    if (isGeneralGroup && !form.channelUuid)
      nextErrors.channelUuid = "กรุณาเลือกช่องทางการขาย";
    // Checked only when it is going to be sent, so an untouched legacy value never blocks
    // an unrelated edit.
    if (isGeneralGroup && storeNumberChanged && form.storeNumber.trim()) {
      const storeNumberError = zStoreNumberError(form.storeNumber.trim());
      if (storeNumberError) nextErrors.storeNumber = storeNumberError;
    }
    if (!form.routeUuid) nextErrors.routeUuid = "กรุณาเลือกสาย";
    if (!form.priceBook) nextErrors.priceBook = "กรุณาเลือกสมุดราคา";
    if (!isCash && !form.creditTerm)
      nextErrors.creditTerm = "กรุณาเลือกเครดิตเทอม";
    if (!form.addressNo.trim())
      nextErrors.addressNo = "กรุณากรอกที่อยู่ (เลขที่)";
    if (form.addressZipcode && !/^\d{5}$/.test(form.addressZipcode)) {
      nextErrors.addressZipcode = "รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก";
    }

    // The bill header is filled in for every customer, so what is typed is format-checked
    // for every customer. Only a customer accounting has actually put on eTax must be
    // COMPLETE: a paper shop is not a shop with missing data, it is a shop nobody has asked
    // yet, and refusing to save one over a blank tax id would block edits to everything else.
    const requireHeader = form.wantTaxInvoice;

    if (requireHeader && !form.invName.trim())
      nextErrors.invName = "กรุณากรอกชื่อสำหรับใบกำกับภาษี";

    // Mirrors validateEtaxBuyerContract on the backend (KTB spec v1.6 §3.2). The rules
    // differ per buyer type, which is the whole reason the type is stored: 13 digits
    // classify themselves, a passport does not. Kept here so the shop is told before the
    // save rather than after — the backend stays the authority either way.
    const taxId = form.invTaxId.trim();
    // An identifier without a stated type is the case that has no right answer: 13 digits
    // classify themselves, a passport does not, and guessing is what sent a passport buyer
    // to KTB as "N/A". Ask once, here, rather than let the save fail at the API.
    if ((form.wantTaxInvoice || taxId) && !form.invTaxIdType) {
      nextErrors.invTaxIdType = "กรุณาเลือกประเภทผู้ซื้อ";
    }
    if (form.invTaxIdType === "TXID" || form.invTaxIdType === "NIDN") {
      if (!taxId) {
        if (requireHeader) nextErrors.invTaxId = "กรุณากรอกเลขที่ผู้เสียภาษี";
      } else if (!/^\d{13}$/.test(taxId)) {
        nextErrors.invTaxId = "เลขที่ผู้เสียภาษีต้องเป็นตัวเลข 13 หลัก";
      } else if (form.invTaxIdType === "TXID" && !taxId.startsWith("0")) {
        nextErrors.invTaxId = "เลขผู้เสียภาษีนิติบุคคลต้องขึ้นต้นด้วย 0";
      } else if (form.invTaxIdType === "NIDN" && !/^[1-8]/.test(taxId)) {
        nextErrors.invTaxId = "เลขบัตรประชาชนต้องขึ้นต้นด้วย 1-8";
      }
    } else if (form.invTaxIdType === "CCPT") {
      if (!taxId) {
        if (requireHeader) nextErrors.invTaxId = "กรุณากรอกเลขหนังสือเดินทาง";
      } else if (taxId.length > 18) {
        nextErrors.invTaxId = "เลขหนังสือเดินทางต้องไม่เกิน 18 ตัวอักษร";
      }
    }
    // "อื่น ๆ" (OTHR) asks for no identifier at all: KTB takes the literal "N/A" there.

    if (form.invTaxIdType === "TXID" && form.invBranchCode.trim() && !/^\d{1,5}$/.test(form.invBranchCode.trim())) {
      nextErrors.invBranchCode = "รหัสสาขาต้องเป็นตัวเลข 1-5 หลัก (00000 = สำนักงานใหญ่)";
    }

    if (!form.sameAddressAsCustomer) {
      if (!form.invAddress.trim()) {
        if (requireHeader)
          nextErrors.invAddress = "กรุณากรอกที่อยู่สำหรับออกใบกำกับภาษี";
      }
      if (!form.invZipcode.trim()) {
        if (requireHeader) nextErrors.invZipcode = "กรุณากรอกรหัสไปรษณีย์";
      } else if (!/^\d{5}$/.test(form.invZipcode)) {
        nextErrors.invZipcode = "รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // On update, drop the customer_code inputs the user did not touch. The drawer submits the
  // whole form, and a resent-but-identical group / route / creditterm used to be enough to
  // put the backend through a code re-derivation on every save.
  const buildUpdatePayload = (): CustomerUpdateRequest => {
    const built = buildPayload(form, isHo, isGeneralGroup, canSetEtaxIntent);
    // Branch is a CREATE-only choice: an existing customer cannot be moved between
    // branches (the backend's update config has no branch_uuid and would strip it).
    delete built.branch_uuid;
    const payload = built as CustomerUpdateRequest;
    const changed = new Set(changedCodeFields);

    if (!changed.has(CODE_AFFECTING_LABELS.customerGroup))
      delete payload.customer_group_uuid;
    if (!changed.has(CODE_AFFECTING_LABELS.channel)) delete payload.channel_uuid;
    if (!changed.has(CODE_AFFECTING_LABELS.route)) delete payload.route_uuid;
    if (isGeneralGroup && storeNumberChanged && !form.storeNumber.trim()) {
      // Z cleared: null tells the backend to drop the value and keep the current code.
      payload.store_number = null;
    } else if (!changed.has(CODE_AFFECTING_LABELS.storeNumber)) {
      delete payload.store_number;
    }

    if (changed.has(CODE_AFFECTING_LABELS.creditTerm)) {
      // Switching a credit customer to cash clears the terms. buildPayload omits the pair
      // entirely in that case, which would read as "unchanged" server-side — state it.
      if (!resolveCreditTerm(form)) {
        payload.creditterm_type = "UNSPECIFIED";
        payload.creditterm = null;
      }
    } else {
      delete payload.creditterm_type;
      delete payload.creditterm;
    }

    // The pin goes only when the user re-pinned the shop. The API now bounds a pin to
    // Thailand, and legacy customers carry pins outside that box (0,0 from the old sync), so
    // resending one untouched refused every other edit on the customer with a lat/lng error
    // the user never caused — and on prod the map does not load, so they cannot re-pin it.
    // An omitted pin is left as stored; the picker always moves both halves together.
    // Sent only when moved, so an untouched shop's save never mentions it; clearing it is
    // stated as null.
    if (form.serviceMode !== (loadedCustomer?.service_mode ?? "")) {
      payload.service_mode = form.serviceMode || null;
    } else {
      delete payload.service_mode;
    }

    const pinUnchanged =
      form.latitude === (loadedCustomer?.latitude ?? null) &&
      form.longitude === (loadedCustomer?.longitude ?? null);
    if (pinUnchanged) {
      delete payload.latitude;
      delete payload.longitude;
    }

    return payload;
  };

  const submit = async () => {
    setConfirmSaveOpen(false);
    setLoading(true);
    try {
      if (customerId) {
        await updateCustomer(customerId, buildUpdatePayload());
      } else {
        await createCustomer(buildPayload(form, isHo, isGeneralGroup, canSetEtaxIntent));
      }
      onSaved();
      closeAfterSave();
    } catch (error) {
      // The API answers a rejected save with a field-by-field account of what is wrong —
      // it collects every failure before refusing, precisely so the form can show them all
      // at once. Discarding that left the user with the interceptor's one-line
      // "The proviced input is invalid format." and nothing to act on, which is the same
      // amount of help as no message at all.
      //
      // Only fields this form actually draws are mapped. A rejection naming something else
      // still reaches the user through the interceptor toast; putting it in `errors` would
      // set a message next to no input and it would never clear.
      //
      // A refused pricebook names the code under `pricebook`, so its reason is the message.
      const rejectedFields = fieldErrorsFrom(error);
      const bindingRefusal = readPricebookBindingRefusal(error);
      if (bindingRefusal) rejectedFields.priceBook = bindingRefusal;
      setErrors((current) => ({ ...current, ...rejectedFields }));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!validate()) return;
    // Editing a code-affecting field re-issues the customer code, which is the key that
    // ties the customer to its stock, demand and sales-report history — confirm first.
    // Switching eTax on commits the company to billed KTB filings, and switching it off sends
    // the customer back to paper — confirm that too. Both go in the one dialog.
    if ((customerId && changedCodeFields.length > 0) || etaxChange) {
      setConfirmSaveOpen(true);
      return;
    }
    await submit();
  };

  const confirmsCodeChange = Boolean(customerId) && changedCodeFields.length > 0;
  const confirmTitle =
    confirmsCodeChange && etaxChange
      ? "ยืนยันการบันทึก"
      : confirmsCodeChange
        ? "ยืนยันการเปลี่ยนรหัสลูกค้า"
        : etaxChange === "on"
          ? "ยืนยันการส่ง E-Tax"
          : "ยืนยันการยกเลิก E-Tax";
  const confirmLabel =
    confirmsCodeChange && etaxChange
      ? "ยืนยันบันทึก"
      : confirmsCodeChange
        ? "ยืนยันเปลี่ยนรหัส"
        : etaxChange === "on"
          ? "ยืนยันส่ง E-Tax"
          : "ยืนยันยกเลิก E-Tax";

  const priceBookHelperText =
    errors.priceBook ||
    (isHo && !shopBranchCompanyId ? "กรุณาเลือกสาขาก่อน" : undefined);

  const inputSx = { mb: 2, [compact]: { mb: 1.25 } };
  const compactInputSx = { mb: 1, [compact]: { mb: 0.75 } };

  // Live preview of how the customer appears on the tax invoice.
  // Mirrors validateEtaxBuyerEmail on the API side — permissive on purpose, since KTB is the
  // authority on what it accepts. Blank is valid: the field is optional.
  const invEmailError = useMemo(() => {
    const value = form.invEmail.trim();
    if (!value) return "";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "รูปแบบอีเมลไม่ถูกต้อง";
    if (value.length > 256) return "อีเมลยาวเกิน 256 ตัวอักษร";
    return "";
  }, [form.invEmail]);

  const taxBlock = useMemo(() => {
    const name = cleanTax(form.invName) || cleanTax(form.shopName);
    const useInv = !form.sameAddressAsCustomer;
    const address = useInv
      ? joinTaxParts([form.invAddress, form.invZipcode])
      : joinTaxParts([
          form.addressNo,
          form.addressStreet,
          form.addressSubdistrict,
          form.addressDistrict,
          form.addressProvince,
          form.addressZipcode,
        ]);
    const taxId = cleanTax(form.invTaxId);
    const branchCode = cleanTax(form.invBranchCode);
    const branchName = cleanTax(form.invBranchName);
    let taxLine = `Tax ID : ${taxId}`;
    if (branchCode) taxLine += ` สาขาที่ ${branchCode}`;
    if (branchName) taxLine += ` ${branchName}`;
    return { name, address, taxLine };
  }, [
    form.invName,
    form.shopName,
    form.sameAddressAsCustomer,
    form.invAddress,
    form.invZipcode,
    form.addressNo,
    form.addressStreet,
    form.addressSubdistrict,
    form.addressDistrict,
    form.addressProvince,
    form.addressZipcode,
    form.invTaxId,
    form.invBranchCode,
    form.invBranchName,
  ]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "100%", md: 720 },
            maxWidth: "100%",
            p: 0,
          },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ p: 3, pb: 0, [compact]: { p: 2, pb: 0 } }}>
          <Typography variant="body1" fontWeight={600} gutterBottom>
            {customerId ? "แก้ไขข้อมูลลูกค้า" : "สร้างลูกค้าใหม่"}
          </Typography>
          <Divider />
        </Box>

        <Box sx={{ p: 3, [compact]: { p: 2 }, overflowY: "auto", flex: 1 }}>
          {isPreparingForm ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {hasSettingsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {SETTINGS_LOAD_ERROR}
                </Alert>
              )}
              <SectionTitle>ข้อมูลทั่วไป</SectionTitle>

              {customerId && loadedCustomer?.customer_code && (
                <>
                  <FieldLabel>รหัสลูกค้า</FieldLabel>
                  <Box
                    sx={{
                      mb: 2,
                      [compact]: { mb: 1.25 },
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      bgcolor: (theme) => theme.palette.background.default,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography
                      sx={{ fontFamily: "monospace", fontSize: 14 }}
                    >
                      {loadedCustomer.customer_code}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.25 }}
                    >
                      ระบบสร้างให้อัตโนมัติ · แก้ไขโดยตรงไม่ได้
                    </Typography>
                  </Box>
                </>
              )}

              <FieldLabel required>ชื่อลูกค้า/ชื่อร้านค้า</FieldLabel>
              <TextField
                size="small"
                fullWidth
                value={form.shopName}
                onChange={handleTextChange("shopName", "shopName")}
                placeholder="ชื่อลูกค้า/ชื่อร้านค้า"
                error={Boolean(errors.shopName)}
                helperText={errors.shopName}
                sx={inputSx}
              />

              <FieldLabel required>ชื่อผู้ติดต่อ</FieldLabel>
              <TextField
                size="small"
                fullWidth
                value={form.contactName}
                onChange={handleTextChange("contactName", "contactName")}
                placeholder="ข้อมูล"
                error={Boolean(errors.contactName)}
                helperText={errors.contactName}
                sx={inputSx}
              />

              <FieldLabel>เบอร์โทรติดต่อ</FieldLabel>
              <TextField
                size="small"
                fullWidth
                value={form.phone}
                onChange={handleDigitChange("phone", "phone", 10)}
                placeholder="ข้อมูล"
                slotProps={{
                  htmlInput: { inputMode: "numeric", maxLength: 10 },
                }}
                error={Boolean(errors.phone)}
                helperText={errors.phone}
                sx={inputSx}
              />

              <FieldLabel required>หมวดลูกค้า</FieldLabel>
              <TextField
                size="small"
                select
                fullWidth
                value={form.customerGroupUuid}
                onChange={(event) => {
                  const nextGroupUuid = event.target.value;
                  setForm((prev) => ({
                    ...prev,
                    customerGroupUuid: nextGroupUuid,
                    // Channel only applies to the general group.
                    channelUuid: isGeneralGroupUuid(nextGroupUuid)
                      ? prev.channelUuid
                      : "",
                  }));
                  clearError("customerGroupUuid");
                  clearError("channelUuid");
                }}
                error={Boolean(errors.customerGroupUuid)}
                helperText={errors.customerGroupUuid}
                sx={inputSx}
              >
                <MenuItem value="">เลือกหมวดลูกค้า</MenuItem>
                {customerGroups.map((group) => (
                  <MenuItem key={group.uuid} value={group.uuid}>
                    {group.name}
                  </MenuItem>
                ))}
              </TextField>
              <CodeAffectingHint />

              {isGeneralGroup && (
                <>
                  <FieldLabel required>ช่องทางการขาย</FieldLabel>
                  <TextField
                    size="small"
                    select
                    fullWidth
                    value={form.channelUuid}
                    onChange={(event) => {
                      setField("channelUuid", event.target.value);
                      clearError("channelUuid");
                    }}
                    error={Boolean(errors.channelUuid)}
                    helperText={errors.channelUuid}
                    sx={inputSx}
                  >
                    <MenuItem value="">เลือกช่องทางการขาย</MenuItem>
                    {channels.map((channel) => (
                      <MenuItem key={channel.uuid} value={channel.uuid}>
                        {channel.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <CodeAffectingHint />
                </>
              )}

              {showStoreNumber && (
                <>
                  <FieldLabel>หมายเลขร้าน</FieldLabel>
                  <TextField
                    size="small"
                    fullWidth
                    value={form.storeNumber}
                    onChange={handleTextChange("storeNumber", "storeNumber")}
                    placeholder="หมายเลขร้าน (ไม่ระบุ = ระบบสร้างให้อัตโนมัติ)"
                    error={Boolean(errors.storeNumber)}
                    helperText={
                      errors.storeNumber ||
                      (isGeneralGroup ? Z_STORE_NUMBER_HELPER : undefined)
                    }
                    sx={inputSx}
                  />
                  <CodeAffectingHint />
                </>
              )}

              {isHo && (
                <>
                  <FieldLabel required>สาขา</FieldLabel>
                  <TextField
                    size="small"
                    select
                    fullWidth
                    value={form.branchUuid}
                    onChange={(event) => {
                      setForm((prev) => ({
                        ...prev,
                        branchUuid: event.target.value,
                        routeUuid: "",
                        priceBook: "",
                      }));
                      clearError("branchUuid");
                    }}
                    // Branch is chosen at creation only — an existing customer cannot be
                    // moved between branches (the update endpoint has no branch field).
                    disabled={Boolean(customerId)}
                    error={Boolean(errors.branchUuid)}
                    helperText={
                      errors.branchUuid ||
                      (customerId ? "ย้ายสาขาไม่ได้หลังสร้างลูกค้าแล้ว" : undefined)
                    }
                    sx={inputSx}
                  >
                    <MenuItem value="">เลือกสาขา</MenuItem>
                    {branches.map((branch) => (
                      <MenuItem key={branch.uuid} value={branch.uuid}>
                        {branch.branch_code} {branch.branch_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              )}

              <FieldLabel required>สาย (Route)</FieldLabel>
              <TextField
                size="small"
                select
                fullWidth
                value={form.routeUuid}
                onChange={(event) => {
                  setField("routeUuid", event.target.value);
                  clearError("routeUuid");
                }}
                disabled={isHo && !form.branchUuid}
                error={Boolean(errors.routeUuid)}
                helperText={
                  errors.routeUuid ||
                  (isHo && !form.branchUuid ? "กรุณาเลือกสาขาก่อน" : undefined)
                }
                sx={inputSx}
              >
                <MenuItem value="">เลือกสาย</MenuItem>
                {routes.map((route) => (
                  <MenuItem key={route.uuid} value={route.uuid}>
                    {route.route_code} {route.route_name}
                  </MenuItem>
                ))}
              </TextField>
              <CodeAffectingHint />

              <FieldLabel>แบบบริการออเดอร์พิเศษ</FieldLabel>
              <TextField
                size="small"
                select
                fullWidth
                value={form.serviceMode}
                onChange={(event) =>
                  setField("serviceMode", event.target.value as CustomerServiceMode | "")
                }
                helperText="สายที่ระบบเลือกให้ก่อนตอนออกออเดอร์พิเศษ (เปลี่ยนได้ในแต่ละออเดอร์)"
                sx={inputSx}
              >
                {SERVICE_MODE_OPTIONS.map((option) => (
                  <MenuItem key={option.value || "none"} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <FieldLabel required>ประเภทบิล</FieldLabel>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                {BILL_TYPES.map(({ value, label }) => (
                  <Box
                    key={value}
                    onClick={() => setField("billType", value)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                    }}
                  >
                    <Checkbox
                      checked={form.billType === value}
                      disableRipple
                      sx={{ p: 0, pointerEvents: "none" }}
                    />
                    <Typography>{label}</Typography>
                  </Box>
                ))}
              </Box>

              {!isCash && (
                <>
                  <FieldLabel required>เครดิตเทอม</FieldLabel>
                  <TextField
                    size="small"
                    select
                    fullWidth
                    value={form.creditTerm}
                    onChange={(event) => {
                      setField("creditTerm", event.target.value);
                      clearError("creditTerm");
                    }}
                    error={Boolean(errors.creditTerm)}
                    helperText={errors.creditTerm}
                    sx={inputSx}
                  >
                    <MenuItem value="">เลือกเครดิตเทอม</MenuItem>
                    {CREDIT_TERM_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {/* Credit terms only drive the code for the general group (Z);
                      Modern Trade chains keep the same leading digit either way. */}
                  {isGeneralGroup && <CodeAffectingHint />}
                </>
              )}

              <FieldLabel required>ที่อยู่</FieldLabel>
              <TextField
                size="small"
                fullWidth
                value={form.addressNo}
                onChange={handleTextChange("addressNo", "addressNo")}
                placeholder="เลขที่"
                error={Boolean(errors.addressNo)}
                helperText={errors.addressNo}
                sx={compactInputSx}
              />
              <TextField
                size="small"
                fullWidth
                value={form.addressLine2}
                onChange={handleTextChange("addressLine2")}
                placeholder="ที่อยู่ (เพิ่มเติม)"
                sx={compactInputSx}
              />
              <TextField
                size="small"
                fullWidth
                value={form.addressStreet}
                onChange={handleTextChange("addressStreet")}
                placeholder="ถนน"
                sx={compactInputSx}
              />
              <TextField
                size="small"
                fullWidth
                value={form.addressProvince}
                onChange={handleTextChange("addressProvince")}
                placeholder="จังหวัด"
                sx={compactInputSx}
              />
              <TextField
                size="small"
                fullWidth
                value={form.addressSubdistrict}
                onChange={handleTextChange("addressSubdistrict")}
                placeholder="แขวง/ตำบล"
                sx={compactInputSx}
              />
              <TextField
                size="small"
                fullWidth
                value={form.addressDistrict}
                onChange={handleTextChange("addressDistrict")}
                placeholder="เขต/อำเภอ"
                sx={compactInputSx}
              />
              <TextField
                size="small"
                fullWidth
                value={form.addressZipcode}
                onChange={handleDigitChange(
                  "addressZipcode",
                  "addressZipcode",
                  5,
                )}
                placeholder="รหัสไปรษณีย์"
                slotProps={{
                  htmlInput: { inputMode: "numeric", maxLength: 5 },
                }}
                error={Boolean(errors.addressZipcode)}
                helperText={errors.addressZipcode}
                sx={inputSx}
              />

              <Divider sx={{ my: 3, [compact]: { my: 2 } }} />

              <FieldLabel required>ประเภท VAT ในราคาสินค้า</FieldLabel>
              <TextField
                size="small"
                select
                fullWidth
                value={form.vatType}
                onChange={(event) =>
                  setField("vatType", event.target.value as CustomerTaxtype)
                }
                sx={inputSx}
              >
                <MenuItem value="IN">IN-Include Vat</MenuItem>
                <MenuItem value="EX">EX-Exclude Vat</MenuItem>
              </TextField>

              <FieldLabel required>สมุดราคา</FieldLabel>
              <TextField
                size="small"
                select
                fullWidth
                value={form.priceBook}
                onChange={(event) => {
                  setField("priceBook", event.target.value);
                  clearError("priceBook");
                }}
                disabled={!shopBranchCompanyId}
                error={Boolean(errors.priceBook)}
                helperText={priceBookHelperText}
                sx={inputSx}
              >
                <MenuItem value="">เลือกสมุดราคา</MenuItem>
                {form.priceBook &&
                !pricebooks.some((pb) => pb.code === form.priceBook) ? (
                  <MenuItem value={form.priceBook}>{form.priceBook}</MenuItem>
                ) : null}
                {pricebooks.map((pb) => (
                  <MenuItem key={pb.uuid} value={pb.code}>
                    {pb.name}
                  </MenuItem>
                ))}
              </TextField>

              <Divider sx={{ my: 3, [compact]: { my: 2 } }} />
              <SectionTitle>หัวบิล / ใบกำกับภาษี</SectionTitle>

              {canSetEtaxIntent && <EtaxCostWarning />}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 2,
                  cursor: "pointer",
                }}
                onClick={() =>
                  canSetEtaxIntent &&
                  setField("wantTaxInvoice", !form.wantTaxInvoice)
                }
              >
                <Checkbox
                  checked={form.wantTaxInvoice}
                  disabled={!canSetEtaxIntent}
                  disableRipple
                  sx={{ p: 0, pointerEvents: "none" }}
                />
                <Typography color={canSetEtaxIntent ? undefined : "text.disabled"}>
                  ต้องการใบกำกับภาษีอิเล็กทรอนิกส์
                </Typography>
              </Box>
              {!canSetEtaxIntent && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                  บัญชี การเงินสาขา หรือสำนักงานใหญ่ เท่านั้นที่เปลี่ยนได้
                </Typography>
              )}
              {/* Shown whether or not the customer is on eTax. A paper bill prints the same
                  header, so hiding the block behind the eTax flag hid the fields the
                  customer is looking at on their own bill — and meant the details had to be
                  collected from scratch the day accounting switched them over. Nothing
                  below is required until that switch is made. */}
              {!form.wantTaxInvoice && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 2 }}
                >
                  กรอกหรือไม่กรอกก็ได้ ข้อมูลนี้ใช้พิมพ์หัวบิล และพร้อมใช้ทันทีเมื่อเปิด e-Tax ให้ลูกค้ารายนี้
                </Typography>
              )}

              <FieldLabel required={form.wantTaxInvoice}>
                ประเภทผู้ซื้อ
              </FieldLabel>
              <TextField
                select
                size="small"
                fullWidth
                value={form.invTaxIdType}
                onChange={(event) => {
                  setField("invTaxIdType", event.target.value as CustomerTaxIdType);
                  clearError("invTaxIdType");
                }}
                error={Boolean(errors.invTaxIdType)}
                helperText={errors.invTaxIdType}
                sx={inputSx}
              >
                <MenuItem value="">ไม่ระบุ</MenuItem>
                {BUYER_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <FieldLabel required={form.wantTaxInvoice}>
                ชื่อสำหรับใบกำกับภาษี
              </FieldLabel>
              <TextField
                size="small"
                fullWidth
                value={form.invName}
                onChange={handleTextChange("invName", "invName")}
                placeholder="ชื่อสำหรับใบกำกับภาษี"
                error={Boolean(errors.invName)}
                helperText={errors.invName}
                sx={inputSx}
              />

              <FieldLabel
                required={form.wantTaxInvoice && form.invTaxIdType !== "OTHR"}
              >
                {form.invTaxIdType ? BUYER_ID_LABEL[form.invTaxIdType] : "เลขผู้ซื้อ"}
              </FieldLabel>
              {/* A digits-only field for the two 13-digit types, free text for a passport.
                  Forcing digits on a passport would silently eat the letters in it. */}
              {form.invTaxIdType === "TXID" || form.invTaxIdType === "NIDN" ? (
                <TextField
                  size="small"
                  fullWidth
                  value={form.invTaxId}
                  onChange={handleDigitChange("invTaxId", "invTaxId", 13)}
                  placeholder={
                    form.invTaxIdType === "TXID"
                      ? "13 หลัก ขึ้นต้นด้วย 0"
                      : "13 หลัก ขึ้นต้นด้วย 1-8"
                  }
                  slotProps={{
                    htmlInput: { inputMode: "numeric", maxLength: 13 },
                  }}
                  error={Boolean(errors.invTaxId)}
                  helperText={errors.invTaxId}
                  sx={inputSx}
                />
              ) : (
                <TextField
                  size="small"
                  fullWidth
                  value={form.invTaxId}
                  onChange={handleTextChange("invTaxId", "invTaxId")}
                  placeholder={
                    form.invTaxIdType === "CCPT"
                      ? "เลขหนังสือเดินทาง ไม่เกิน 18 ตัวอักษร"
                      : "ไม่บังคับกรอก — ระบบจะส่ง N/A"
                  }
                  slotProps={{ htmlInput: { maxLength: 18 } }}
                  error={Boolean(errors.invTaxId)}
                  helperText={errors.invTaxId}
                  sx={inputSx}
                />
              )}

              {/* Hidden, not disabled, for the three types that must not carry one: KTB
                  rejects a trader id built with a branch for anyone but a company, and a
                  greyed-out box still invites the question of what belongs in it. */}
              {form.invTaxIdType === "TXID" && (
                <>
                  <FieldLabel>รหัสสาขา</FieldLabel>
                  <TextField
                    size="small"
                    fullWidth
                    value={form.invBranchCode}
                    onChange={handleTextChange("invBranchCode", "invBranchCode")}
                    placeholder="00000 = สำนักงานใหญ่"
                    error={Boolean(errors.invBranchCode)}
                    helperText={
                      errors.invBranchCode ||
                      "เว้นว่างได้ ระบบจะออกให้สำนักงานใหญ่ (00000)"
                    }
                    sx={inputSx}
                  />
                </>
              )}

              <FieldLabel>ชื่อสาขา</FieldLabel>
              <TextField
                size="small"
                fullWidth
                value={form.invBranchName}
                onChange={handleTextChange("invBranchName")}
                placeholder="ชื่อสาขา (เช่น สำนักงานใหญ่)"
                sx={inputSx}
              />

              <FieldLabel>อีเมลรับใบกำกับภาษี</FieldLabel>
              <TextField
                size="small"
                fullWidth
                type="email"
                value={form.invEmail}
                onChange={handleTextChange("invEmail")}
                placeholder="อีเมลสำหรับรับใบกำกับภาษีจาก KTB"
                error={Boolean(invEmailError)}
                helperText={
                  invEmailError ||
                  "ไม่กรอกก็บันทึกได้ แต่ลูกค้าจะไม่ได้รับใบกำกับภาษีทางอีเมล"
                }
                sx={inputSx}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 2,
                  cursor: "pointer",
                }}
                onClick={() =>
                  setField("sameAddressAsCustomer", !form.sameAddressAsCustomer)
                }
              >
                <Checkbox
                  checked={form.sameAddressAsCustomer}
                  disableRipple
                  sx={{ p: 0, pointerEvents: "none" }}
                />
                <Typography>ใช้ที่อยู่เดียวกับข้อมูลลูกค้า</Typography>
              </Box>

              {!form.sameAddressAsCustomer && (
                <>
                  <FieldLabel required={form.wantTaxInvoice}>
                    ที่อยู่สำหรับใบกำกับภาษี
                  </FieldLabel>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={form.invAddress}
                    onChange={handleTextChange("invAddress", "invAddress")}
                    placeholder="ที่อยู่สำหรับใบกำกับภาษี"
                    error={Boolean(errors.invAddress)}
                    helperText={errors.invAddress}
                    sx={inputSx}
                  />

                  <FieldLabel required={form.wantTaxInvoice}>รหัสไปรษณีย์</FieldLabel>
                  <TextField
                    size="small"
                    fullWidth
                    value={form.invZipcode}
                    onChange={handleDigitChange("invZipcode", "invZipcode", 5)}
                    placeholder="รหัสไปรษณีย์"
                    slotProps={{
                      htmlInput: { inputMode: "numeric", maxLength: 5 },
                    }}
                    error={Boolean(errors.invZipcode)}
                    helperText={errors.invZipcode}
                    sx={inputSx}
                  />
                </>
              )}

              <Divider sx={{ my: 3, [compact]: { my: 2 } }} />
              <SectionTitle>ตัวอย่างหัวบิล</SectionTitle>
              <Box
                sx={{
                  mb: 3,
                  [compact]: { mb: 2 },
                  p: 2,
                  borderRadius: 1,
                  bgcolor: (theme) => theme.palette.background.default,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  ลูกค้า : {taxBlock.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  ที่อยู่ : {taxBlock.address}
                </Typography>
                <Typography variant="body2">{taxBlock.taxLine}</Typography>
              </Box>

              <Divider sx={{ my: 3, [compact]: { my: 2 } }} />
              <SectionTitle>ตำแหน่งบนแผนที่</SectionTitle>
              <CustomerLocationPicker
                latitude={form.latitude}
                longitude={form.longitude}
                onChange={({ latitude, longitude }) =>
                  setForm((prev) => ({ ...prev, latitude, longitude }))
                }
              />
            </>
          )}
        </Box>

        <Box
          sx={{
            p: 2,
            [compact]: { px: 2, py: 1.25 },
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button disabled={loading} onClick={handleClose}>
            ยกเลิก
          </Button>
          <Button
            loading={loading}
            disabled={
              isPreparingForm || hasSettingsError || Boolean(invEmailError)
            }
            variant="contained"
            onClick={handleSave}
          >
            ยืนยัน
          </Button>
        </Box>
      </Box>

      <Dialog
        open={confirmSaveOpen}
        onClose={() => setConfirmSaveOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          {confirmTitle}
        </DialogTitle>
        <DialogContent>
          {etaxChange === "on" && <EtaxCostWarning />}
          {etaxChange === "off" && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                คุณกำลังยกเลิกการส่ง E-Tax ลูกค้ารายนี้จะกลับไปรับบิลกระดาษ
                และจะไม่ได้รับใบกำกับภาษีอิเล็กทรอนิกส์อีก
              </Typography>
            </Alert>
          )}
          {confirmsCodeChange && (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                คุณกำลังแก้ไขข้อมูลที่ใช้สร้างรหัสลูกค้า ระบบจะออกรหัสใหม่ให้ลูกค้ารายนี้เมื่อบันทึก
              </Typography>
              <Box
                component="ul"
                sx={{ pl: 2.5, my: 0, "& li": { mb: 0.25 } }}
              >
                {changedCodeFields.map((label) => (
                  <li key={label}>
                    <Typography variant="body2">{label}</Typography>
                  </li>
                ))}
              </Box>
              {loadedCustomer?.customer_code && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1.5 }}
                >
                  รหัสปัจจุบัน:{" "}
                  <Box component="span" sx={{ fontFamily: "monospace" }}>
                    {loadedCustomer.customer_code}
                  </Box>
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                รหัสลูกค้าใช้เชื่อมประวัติสต๊อก ยอดสั่ง และรายงานการขาย
                การเปลี่ยนรหัสจะทำให้ข้อมูลเดิมไม่ผูกกับลูกค้ารายนี้อีก
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmSaveOpen(false)}>ยกเลิก</Button>
          <Button variant="contained" onClick={submit} loading={loading}>
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
